import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Download, Printer, Calendar, Landmark, Building2, Loader2, AlertCircle, FileSpreadsheet } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { downloadAuthenticatedFile } from '../../services/reportsApi';
import { buildApiUrl } from '../../utils/apiSetup';

const MONTHS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const currentYr = new Date().getFullYear();
const currentMo = new Date().getMonth() + 1;
const YEARS = Array.from({ length: Math.max(1, currentYr - 2025 + 2) }, (_, i) => String(2025 + i));

const BankLoanSummaryReportView = ({
  initialYear = String(currentYr),
  initialMonth = String(currentMo),
  initialDepartment = 'ALL',
  departmentsList = [],
}) => {
  const { hasPermission } = useAuth();
  const printRef = useRef(null);

  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(initialMonth);
  const [department, setDepartment] = useState(initialDepartment);
  const [selectedBankId, setSelectedBankId] = useState('');

  const [banksList, setBanksList] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [error, setError] = useState('');

  // Fetch banks master list for dropdown
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const res = await fetch(buildApiUrl('/api/v1/bank-loans/banks'), {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        });
        if (res.ok) {
          const banks = await res.json();
          setBanksList(Array.isArray(banks) ? banks : []);
        }
      } catch (err) {
        console.error('Error fetching banks list:', err);
      }
    };
    fetchBanks();
  }, []);

  // Department options
  const departmentOptions = useMemo(() => {
    const names = departmentsList.map((d) => d.name || d.code).filter(Boolean);
    return ['ALL', ...Array.from(new Set(names)).sort()];
  }, [departmentsList]);

  // Load report data from backend
  useEffect(() => {
    const controller = new AbortController();
    const loadReportData = async () => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({ month, year });
        if (department && department !== 'ALL') {
          params.set('department', department);
        }
        if (selectedBankId) {
          params.set('bank_id', selectedBankId);
        }

        const response = await fetch(buildApiUrl(`/api/v1/reports/bank-loan-summary?${params}`), {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
          signal: controller.signal,
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.detail || 'Failed to load Bank Loan Summary report');
        }

        const json = await response.json();
        setData(json);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message || 'Error loading Bank Loan Summary');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    if (month && year) {
      loadReportData();
    }

    return () => controller.abort();
  }, [month, year, department, selectedBankId]);

  // Download PDF
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const params = new URLSearchParams({ month, year });
      if (department && department !== 'ALL') params.set('department', department);
      if (selectedBankId) params.set('bank_id', selectedBankId);

      const monthLabel = MONTHS.find((m) => m.value === String(month))?.label || month;
      const deptSuffix = department && department !== 'ALL' ? `_${department.replace(/\s+/g, '_')}` : '';
      await downloadAuthenticatedFile(
        `/api/v1/reports/bank-loan-summary-pdf?${params}`,
        `Bank_Loan_Summary_${monthLabel}_${year}${deptSuffix}.pdf`
      );
    } catch (err) {
      setError(err.message || 'Failed to download PDF report');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Download Excel
  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const params = new URLSearchParams({ month, year });
      if (department && department !== 'ALL') params.set('department', department);
      if (selectedBankId) params.set('bank_id', selectedBankId);

      const monthLabel = MONTHS.find((m) => m.value === String(month))?.label || month;
      const deptSuffix = department && department !== 'ALL' ? `_${department.replace(/\s+/g, '_')}` : '';
      await downloadAuthenticatedFile(
        `/api/v1/reports/bank-loan-summary-excel?${params}`,
        `Bank_Loan_Summary_${monthLabel}_${year}${deptSuffix}.xlsx`
      );
    } catch (err) {
      setError(err.message || 'Failed to download Excel report');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const groups = data?.groups || [];
  const grandTotal = data?.grand_total || 0;
  const monthLabel = MONTHS.find((m) => m.value === String(month))?.label || month;

  return (
    <div className="space-y-6">
      {/* Filter / Controls Bar (Screen Only) */}
      <div className="print:hidden flex flex-wrap items-end justify-between gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          {/* Year */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
              Year
            </label>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 text-gray-400 mr-1.5" />
              <select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-bold text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white min-w-[90px]"
              >
                {YEARS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Month */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
              Month
            </label>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-bold text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white min-w-[130px]"
            >
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          {/* Department */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
              Department
            </label>
            <div className="flex items-center">
              <Building2 className="w-4 h-4 text-gray-400 mr-1.5" />
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-bold text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white min-w-[170px]"
              >
                {departmentOptions.map((d) => (
                  <option key={d} value={d}>
                    {d === 'ALL' ? 'All Departments' : d}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Bank / Society Filter */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
              Bank / Patsanstha
            </label>
            <div className="flex items-center">
              <Landmark className="w-4 h-4 text-gray-400 mr-1.5" />
              <select
                value={selectedBankId}
                onChange={(e) => setSelectedBankId(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-bold text-gray-800 focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white min-w-[180px]"
              >
                <option value="">All Banks / Societies</option>
                {banksList.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.bank_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-2.5">
          <button
            onClick={handlePrint}
            disabled={loading || groups.length === 0}
            className="flex items-center px-3.5 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50 transition-colors shadow-sm font-medium"
          >
            <Printer className="w-4 h-4 mr-1.5" />
            Print
          </button>

          {hasPermission('report:export') && (
            <>
              <button
                onClick={handleDownloadExcel}
                disabled={loading || downloadingExcel || groups.length === 0}
                className="flex items-center px-3.5 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm font-medium"
              >
                {downloadingExcel ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4 mr-1.5" />
                )}
                Excel
              </button>

              <button
                onClick={handleDownloadPdf}
                disabled={loading || downloadingPdf || groups.length === 0}
                className="flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm font-semibold"
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-1.5" />
                )}
                Download PDF
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Main Printed Document Preview Container */}
      <div ref={printRef} className="print-area space-y-8">
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-16 flex flex-col items-center justify-center space-y-3">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            <p className="text-sm font-medium text-gray-500 font-mono">Loading Bank Loan Summary records...</p>
          </div>
        ) : groups.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500 font-mono">
            No bank loan summary records found for {monthLabel} {year}.
          </div>
        ) : (
          groups.map((group, gIdx) => (
            <div
              key={`${group.department_name}-${group.bank_id}-${gIdx}`}
              className="bg-white border border-gray-300 shadow-md p-8 sm:p-10 rounded-sm font-mono text-gray-900 transition-all page-break-after"
              style={{ fontFamily: "'Courier New', Courier, monospace" }}
            >
              {/* Document Header matching physical sheet */}
              <div className="flex flex-wrap items-start justify-between border-b border-gray-800 pb-3 mb-4">
                <div className="text-sm font-bold tracking-wider">
                  DEPARTMENT - {group.department_name}
                </div>
                <div className="text-sm font-bold tracking-wider uppercase text-right">
                  BANK LOAN SUMMARY FOR THE MONTH OF {group.month_name} {group.year}
                </div>
              </div>

              {/* Bank Name Header */}
              <div className="text-sm font-bold tracking-wider mb-6">
                BANK NAME - {group.bank_name}
              </div>

              {/* Data Table */}
              <table className="w-full border-collapse text-xs sm:text-sm">
                <thead>
                  <tr className="border-t border-b border-black text-left font-bold">
                    <th className="py-2.5 px-3 w-16">SR.NO.</th>
                    <th className="py-2.5 px-3 w-36">EMPLOYEE CODE</th>
                    <th className="py-2.5 px-3">NAME</th>
                    <th className="py-2.5 px-3 text-right w-36">AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((row) => (
                    <tr key={`${row.employee_id}-${row.loan_id}`} className="border-b border-gray-100 hover:bg-gray-50/50">
                      <td className="py-2 px-3">{row.sr_no}</td>
                      <td className="py-2 px-3 font-semibold">{row.employee_code}</td>
                      <td className="py-2 px-3 tracking-wide">{row.name}</td>
                      <td className="py-2 px-3 text-right tabular-nums">{row.amount}</td>
                    </tr>
                  ))}

                  {/* Group Subtotal Row */}
                  <tr className="border-t border-b border-black font-bold">
                    <td className="py-2 px-3" />
                    <td className="py-2 px-3" />
                    <td className="py-2 px-3 text-right pr-6">Total</td>
                    <td className="py-2 px-3 text-right tabular-nums">{group.total}</td>
                  </tr>
                </tbody>
              </table>

              {/* Grand Total and Footer */}
              <div className="mt-8 pt-4 border-t border-gray-400">
                <div className="flex items-center justify-between text-sm font-bold">
                  <span>Grand Total</span>
                  <span className="tabular-nums">{grandTotal}</span>
                </div>
                <div className="text-right text-xs text-gray-700 mt-6">
                  Page Number : {gIdx + 1}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Print Stylesheet */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print-area, .print-area * {
            visibility: visible;
          }
          .print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 0;
          }
          .page-break-after {
            page-break-after: always;
            break-after: page;
          }
        }
      `}</style>
    </div>
  );
};

export default BankLoanSummaryReportView;
